import React, { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Horizontal thumbnail strip.
 * - `images` : array of data URLs.
 * - `onRemove(i)` : optional; when supplied a delete pill is shown per thumb.
 * - `size`     : thumb size in px (default 84).
 * Tap a thumbnail to open the full-screen viewer with prev/next controls.
 */
export function ImageStrip({ images = [], onRemove, size = 84, testid = "image-strip" }) {
  const [openIdx, setOpenIdx] = useState(null);
  if (!images || images.length === 0) return null;
  return (
    <>
      <div
        data-testid={testid}
        className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1"
      >
        {images.map((src, i) => (
          <div
            key={i}
            style={{ width: size, height: size }}
            className="relative shrink-0 rounded-xl overflow-hidden border border-slate-200 bg-slate-50"
          >
            <button
              type="button"
              onClick={() => setOpenIdx(i)}
              data-testid={`${testid}-open-${i}`}
              className="w-full h-full"
              aria-label={`Open image ${i + 1}`}
            >
              <img src={src} alt={`Attachment ${i + 1}`} className="w-full h-full object-cover" />
            </button>
            {onRemove && (
              <button
                type="button"
                onClick={() => onRemove(i)}
                data-testid={`${testid}-remove-${i}`}
                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white grid place-items-center hover:bg-black/80"
                aria-label={`Remove image ${i + 1}`}
              >
                <X className="w-3 h-3" strokeWidth={2.6} />
              </button>
            )}
            <span className="absolute bottom-0.5 left-0.5 text-[9px] font-bold text-white bg-black/50 rounded px-1 tabular-nums">
              {i + 1}/{images.length}
            </span>
          </div>
        ))}
      </div>

      {openIdx !== null && (
        <div
          data-testid={`${testid}-viewer`}
          className="fixed inset-0 z-[70] bg-black/90 grid place-items-center p-4"
          onClick={() => setOpenIdx(null)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setOpenIdx(null); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white grid place-items-center hover:bg-white/20"
            aria-label="Close"
            data-testid={`${testid}-viewer-close`}
          >
            <X className="w-5 h-5" />
          </button>
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setOpenIdx((v) => (v > 0 ? v - 1 : images.length - 1)); }}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 text-white grid place-items-center hover:bg-white/20"
                aria-label="Previous image"
                data-testid={`${testid}-viewer-prev`}
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setOpenIdx((v) => (v < images.length - 1 ? v + 1 : 0)); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 text-white grid place-items-center hover:bg-white/20"
                aria-label="Next image"
                data-testid={`${testid}-viewer-next`}
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <img
            src={images[openIdx]}
            alt={`Attachment ${openIdx + 1}`}
            className="max-w-full max-h-full object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
          {images.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs font-semibold text-white bg-black/50 rounded-full px-3 py-1 tabular-nums">
              {openIdx + 1} / {images.length}
            </div>
          )}
        </div>
      )}
    </>
  );
}
